import PropTypes from "prop-types"
import React, { useEffect, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"

import Button from "src/components/Button"
import Loader from "src/components/Loader"
import Modal from "src/components/Modal"

import {
  generateInvoice,
  getBillableJobs,
  getInvoicePreview,
} from "src/api/Service/WorkOrders"

import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"
import { formatDateOnly, parseDateOnly } from "src/utils/date_only"

import GenerateInvoiceDetailsStep from "./GenerateInvoiceDetailsStep"
import GenerateInvoiceReviewStep from "./GenerateInvoiceReviewStep"
import GenerateInvoiceSelectJobsStep from "./GenerateInvoiceSelectJobsStep"
import GenerateInvoiceStepper from "./GenerateInvoiceStepper"

const GenerateInvoiceModal = ({
  isOpen,
  onClose,
  workOrder,
  workOrderSequentialId,
}) => {
  const { marinaSlug, presentmentCurrency } = useMarina()
  const showToast = useToast()
  const queryClient = useQueryClient()
  const tracker = useTracker()
  const [step, setStep] = useState("select")

  // Default the due date to the marina's today (server-provided), not the
  // browser's clock — both must agree on what "today" is for this marina.
  const defaultDueDate = () =>
    parseDateOnly(workOrder.marinaToday) ?? new Date()

  const methods = useForm({
    defaultValues: {
      selectedJobIds: [],
      dueDate: defaultDueDate(),
      sendEmail: true,
    },
  })

  const { data: jobs = [], isLoading } = useQuery({
    queryKey: ["billableJobs", marinaSlug, workOrderSequentialId],
    queryFn: () => getBillableJobs(marinaSlug, workOrderSequentialId),
    enabled: isOpen,
    // A focus refetch returning a new array reference would re-run the
    // default-select effect below and wipe the user's job deselections.
    refetchOnWindowFocus: false,
  })

  // Default to all billable jobs selected once they load.
  useEffect(() => {
    if (jobs.length)
      methods.setValue(
        "selectedJobIds",
        jobs.map((job) => job.id)
      )
  }, [jobs, methods])

  const selectedJobIds = methods.watch("selectedJobIds")

  // Server computes the full invoice summary for the selected jobs.
  const { data: preview, isLoading: isPreviewLoading } = useQuery({
    queryKey: [
      "invoicePreview",
      marinaSlug,
      workOrderSequentialId,
      selectedJobIds,
    ],
    queryFn: () =>
      getInvoicePreview(marinaSlug, workOrderSequentialId, selectedJobIds),
    enabled: isOpen && step === "review" && selectedJobIds.length > 0,
    refetchOnWindowFocus: false,
  })

  const resetAll = () => {
    setStep("select")
    methods.reset({
      selectedJobIds: [],
      dueDate: defaultDueDate(),
      sendEmail: true,
    })
  }

  const { mutate: submit, isLoading: isSubmitting } = useMutation(
    (values) =>
      generateInvoice(marinaSlug, workOrderSequentialId, {
        jobIds: values.selectedJobIds,
        dueDate: formatDateOnly(values.dueDate),
        sendEmail: values.sendEmail,
      }),
    {
      onSuccess: (invoice) => {
        tracker.trackEvent("work_orders:generate_invoice:confirmed", {
          workOrderSequentialId,
          invoiceId: invoice.id,
          totalAmount: invoice.totalAmount,
          jobCount: selectedJobIds.length,
        })
        queryClient.invalidateQueries([
          "workOrder",
          marinaSlug,
          workOrderSequentialId,
        ])
        onClose()
        showToast(
          `Invoice created for ${formatCurrencyFromMinorUnits({
            amount: invoice.totalAmount,
            currency: presentmentCurrency,
          })}.`,
          { type: "success" }
        )
      },
      onError: (error) => {
        tracker.trackEvent("work_orders:generate_invoice:save_failed", {
          workOrderSequentialId,
          message: error?.message,
        })
        showToast(error.message || "Failed to generate invoice.", {
          type: "error",
        })
      },
    }
  )

  const advanceTo = (next) => {
    tracker.trackEvent("work_orders:generate_invoice:step_advanced", {
      workOrderSequentialId,
      from: step,
      to: next,
    })
    setStep(next)
  }

  const goNext = async () => {
    if (step === "select") {
      const valid = await methods.trigger("selectedJobIds")
      if (!valid) return
      advanceTo("details")
    } else if (step === "details") {
      advanceTo("review")
    }
  }

  const goBack = () => {
    if (step === "review") setStep("details")
    else if (step === "details") setStep("select")
  }

  // Closing via Cancel / backdrop / Esc — distinct from the programmatic close
  // on a successful generate (which calls onClose directly after :confirmed).
  const handleDiscard = () => {
    tracker.trackEvent("work_orders:generate_invoice:discarded", {
      workOrderSequentialId,
      step,
    })
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleDiscard}
      afterLeave={resetAll}
      size="large"
    >
      <Modal.Header
        title="Generate Invoice"
        subtext={`${workOrder.displaySequentialId} · ${workOrder.contact?.name}`}
      />
      <Modal.Body>
        <div className="flex flex-col gap-5 lg:p-4">
          <GenerateInvoiceStepper currentStep={step} />

          <FormProvider {...methods}>
            {isLoading ? (
              <Loader name="jobs" />
            ) : (
              <>
                {step === "select" && (
                  <GenerateInvoiceSelectJobsStep jobs={jobs} />
                )}
                {step === "details" && (
                  <GenerateInvoiceDetailsStep contact={workOrder.contact} />
                )}
                {step === "review" && (
                  <GenerateInvoiceReviewStep
                    preview={preview}
                    workOrder={workOrder}
                    isLoading={isPreviewLoading || !preview}
                  />
                )}
              </>
            )}
          </FormProvider>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <div className="flex justify-between gap-2">
          <Button
            variant="secondary"
            type="button"
            onClick={step === "select" ? handleDiscard : goBack}
            disabled={isSubmitting}
          >
            {step === "select" ? "Cancel" : "Back"}
          </Button>

          {step === "review" ? (
            <Button
              variant="primary"
              type="button"
              onClick={methods.handleSubmit(submit)}
              isLoading={isSubmitting}
              disabled={isSubmitting}
            >
              Generate Invoice
            </Button>
          ) : (
            <Button
              variant="primary"
              type="button"
              onClick={goNext}
              disabled={isLoading}
            >
              Next
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  )
}

GenerateInvoiceModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  workOrder: PropTypes.shape({
    marinaToday: PropTypes.string,
    displaySequentialId: PropTypes.string,
    contact: PropTypes.shape({
      name: PropTypes.string,
      email: PropTypes.string,
    }),
    contactBoat: PropTypes.shape({ name: PropTypes.string }),
  }).isRequired,
  workOrderSequentialId: PropTypes.string.isRequired,
}

export default GenerateInvoiceModal
